import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';
import { AnalyticsDataPoint, TableColumn } from '../types/analytics.types';

interface ExportOptions {
  highlightDeviations?: boolean;
  calculateDeviation?: (row: any, column: any, value: any) => any;
  varianceDisplayMode?: 'absolute' | 'percent';
}

export class ExportService {
  private static formatCellValue(value: any, column: TableColumn): string {
    if (value == null) return '';

    switch (column.dataType) {
      case 'currency':
        const numValue = typeof value === 'number' ? value : parseFloat(value);
        if (isNaN(numValue)) return String(value);
        
        // If column should always display in parentheses (for reductions like discounts/refunds)
        if (column.displayInParentheses) {
          const absValue = Math.abs(numValue);
          const formatted = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(absValue);
          return `(${formatted})`;
        }
        
        // Format negative values with parentheses instead of minus sign
        if (numValue < 0) {
          const absValue = Math.abs(numValue);
          const formatted = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(absValue);
          return `(${formatted})`;
        }
        
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(numValue);

      case 'percentage':
        const pctValue = typeof value === 'number' ? value : parseFloat(value);
        if (isNaN(pctValue)) return ''; // Return empty string for NaN percentages
        
        // Format negative values with parentheses instead of minus sign
        if (pctValue < 0) {
          const absValue = Math.abs(pctValue);
          return `(${absValue.toFixed(1)}%)`;
        }
        
        return `${pctValue.toFixed(1)}%`;

      case 'number':
        const numberValue = typeof value === 'number' ? value : parseFloat(value);
        if (isNaN(numberValue)) return String(value);
        
        const precision = column.precision !== undefined ? column.precision : undefined;
        
        // Format negative values with parentheses instead of minus sign
        if (numberValue < 0) {
          const absValue = Math.abs(numberValue);
          if (precision !== undefined) {
            return `(${absValue.toFixed(precision)})`;
          }
          return `(${absValue.toLocaleString()})`;
        }
        
        if (precision !== undefined) {
          return numberValue.toFixed(precision);
        }
        return numberValue.toLocaleString();

      case 'date':
        if (value instanceof Date) {
          return value.toLocaleDateString();
        }
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
          return new Date(value).toLocaleDateString();
        }
        return String(value);

      default:
        // Special handling for hierarchical fields and order type values
        // Check for hierarchical field names like location_order_type, location_register, etc.
        if (column.field?.includes('_order_type') || column.field === 'order_type' || column.id === 'location_order_type') {
          if (typeof value === 'string') {
            // Handle the specific case of DINEIN -> DINE-IN
            if (value === 'DINEIN') {
              return 'DINE-IN';
            }
            return value;
          }
        }
        
        // For hierarchical fields, try to extract the actual value
        // If value is an object or array, try to get a meaningful string representation
        if (typeof value === 'object' && value !== null) {
          // If it's an array, join it
          if (Array.isArray(value)) {
            return value.join(', ');
          }
          // If it has a name or label property, use that
          if ('name' in value) return String(value.name);
          if ('label' in value) return String(value.label);
          // Otherwise, try to stringify
          return JSON.stringify(value);
        }
        
        return String(value);
    }
  }

  private static formatAbsoluteDelta(val: number, col: TableColumn): string {
    const absVal = Math.abs(val);
    const isNegative = val < 0;
    const wrap = (s: string) => (isNegative ? `(${s})` : s);
    switch (col.dataType) {
      case 'currency':
        return wrap(new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(absVal));
      case 'percentage': {
        const precision = col.precision ?? 1;
        return wrap(`${absVal.toFixed(precision)}%`);
      }
      case 'number': {
        const precision = col.precision !== undefined ? col.precision : undefined;
        const numFormatted = precision !== undefined
          ? absVal.toFixed(precision)
          : absVal.toLocaleString('en-US');
        return wrap(numFormatted);
      }
      default:
        return wrap(absVal.toLocaleString('en-US'));
    }
  }

  private static prepareTableData(
    data: AnalyticsDataPoint[], 
    columns: TableColumn[],
    options?: ExportOptions
  ) {
    // First, flatten hierarchical data to include all levels
    const flattenedData = this.flattenHierarchicalData(data);
    
    // Calculate percentages for percentage columns
    const dataWithPercentages = this.calculatePercentages(flattenedData, columns);
    
    // Check if this is a hierarchical table (has expandIcon column)
    const isHierarchical = columns.some(col => col.expandIcon);
    const firstColumn = columns[0];
    const isHierarchicalFirstColumn = isHierarchical && firstColumn && (
      firstColumn.id === 'location_order_type' || 
      firstColumn.id === 'location_register' ||
      firstColumn.id === 'location_zone' ||
      firstColumn.expandIcon
    );
    
    const headers = columns.map(col => col.header);
    const rows = dataWithPercentages.map(row => 
      columns.map((col, colIndex) => {
        // For hierarchical tables, use _displayValue for the first column
        // This ensures child rows show order_type/register/etc. instead of parent location
        let cellValue = row[col.field];
        if (colIndex === 0 && isHierarchicalFirstColumn && row._displayValue !== undefined) {
          cellValue = row._displayValue;
        }
        
        // Format the base value
        let formattedValue = this.formatCellValue(cellValue, col);
        
        // If variances are enabled and this is a numeric column, add variance
        if (
          options?.highlightDeviations && 
          options?.calculateDeviation &&
          typeof cellValue === 'number' &&
          !col.expandIcon &&
          cellValue !== 0
        ) {
          const deviationResult = options.calculateDeviation(row, col, cellValue);
          
          if (deviationResult !== null) {
            const isDeviationObject = typeof deviationResult === 'object' && 'deltaPercent' in deviationResult;
            const deltaPercent = isDeviationObject ? deviationResult.deltaPercent : deviationResult;
            const deltaAbsolute = isDeviationObject ? deviationResult.deltaAbsolute : null;
            
            // Format variance based on display mode - use column dataType
            let varianceText = '';
            if (options.varianceDisplayMode === 'absolute' && deltaAbsolute !== null) {
              varianceText = this.formatAbsoluteDelta(deltaAbsolute, col);
            } else if (deltaPercent !== null) {
              varianceText = deltaPercent < 0 
                ? `(${Math.abs(deltaPercent).toFixed(1)}%)`
                : `${deltaPercent.toFixed(1)}%`;
            }
            
            // Combine base value and variance (similar to table display)
            if (varianceText) {
              formattedValue = `${formattedValue}\n${varianceText}`;
            }
          }
        }
        
        return formattedValue;
      })
    );
    return { headers, rows };
  }

  /**
   * Flatten hierarchical data to include all child levels, not just visible ones
   * This ensures that order types, registers, and other roll-downs are included in exports
   */
  private static flattenHierarchicalData(data: AnalyticsDataPoint[]): AnalyticsDataPoint[] {
    if (!data || data.length === 0) return [];
    
    // Check if data is hierarchical
    const isHierarchical = data.some(row => row.level !== undefined || row.parentId !== undefined);
    
    // If not hierarchical, return as-is
    if (!isHierarchical) {
      return data;
    }
    
    const result: AnalyticsDataPoint[] = [];
    const processedIds = new Set<string>();
    const rowMap = new Map<string, AnalyticsDataPoint>();
    
    // Build a map of all rows by ID for quick lookup
    data.forEach(row => {
      const rowId = row.id || '';
      if (rowId) {
        rowMap.set(rowId, row);
      }
    });
    
    const addRow = (row: AnalyticsDataPoint) => {
      const rowId = row.id || '';
      if (rowId && processedIds.has(rowId)) {
        return; // Avoid duplicates
      }
      
      if (rowId) {
        processedIds.add(rowId);
      }
      
      // Add the row with its original field values preserved
      // This ensures child rows keep their specific values (order_type, register, etc.)
      result.push({ ...row });
      
      // If this row has children, recursively add them
      // Children are rows that have this row's ID as their parentId
      const children = data.filter(r => r.parentId === rowId);
      children.forEach(child => addRow(child));
    };
    
    // Start with top-level rows (level 0 or no parentId)
    const topLevelRows = data.filter(r => {
      const level = r.level ?? -1;
      return level === 0 || !r.parentId;
    });
    
    topLevelRows.forEach(row => addRow(row));
    
    // Also add any orphaned rows (rows with parentId but parent not in data, or rows without proper hierarchy)
    // This handles edge cases where hierarchy structure might be incomplete
    data.forEach(row => {
      const rowId = row.id || '';
      if (rowId && !processedIds.has(rowId)) {
        // Check if this row has a parent that exists
        if (row.parentId && rowMap.has(row.parentId)) {
          // Parent exists but wasn't processed - add it first, then this row
          const parent = rowMap.get(row.parentId);
          if (parent && !processedIds.has(row.parentId)) {
            addRow(parent);
          }
        }
        // Add this row if still not processed
        if (!processedIds.has(rowId)) {
          result.push({ ...row });
          processedIds.add(rowId);
        }
      }
    });
    
    return result;
  }

  /**
   * Calculate percentages for percentage columns, similar to AnalyticsTable logic
   */
  private static calculatePercentages(data: AnalyticsDataPoint[], columns: TableColumn[]): AnalyticsDataPoint[] {
    if (!data || data.length === 0) return data;
    
    // Find percentage columns
    const percentageColumns = columns.filter(col => 
      col.dataType === 'percentage' || 
      col.field === 'percentage' || 
      (col.field && col.field.includes('percentage')) || 
      (col.field && col.field.includes('percent'))
    );
    
    if (percentageColumns.length === 0) return data;
    
    // Check if data is hierarchical (has level and parentId properties)
    const isHierarchical = data.some(row => row.level !== undefined || row.parentId !== undefined);
    
    let result = [...data];
    
    percentageColumns.forEach(column => {
      // Get formula - can be a string or ColumnFormulaConfig object
      let formulaString: string | undefined;
      if (typeof column.formula === 'string') {
        formulaString = column.formula;
      } else if (column.formula && typeof column.formula === 'object' && 'expression' in column.formula) {
        formulaString = column.formula.expression;
      }
      
      if (!formulaString) {
        // If no formula, try to infer base field from column field name
        // Common patterns: percentage -> total_sales, percent_sales -> sales, etc.
        const baseField = column.field.replace(/percent(age)?/i, '').replace(/^_|_$/g, '') || 'total_sales';
        formulaString = `(${baseField} / total) * 100`;
      }

      // Parse formula to extract base field (e.g., "net_sales" from "(net_sales / total_enterprise_sales) * 100")
      const formulaMatch = formulaString.match(/\(?\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\/\s*[^)]+\)?/);
      const baseField = formulaMatch ? formulaMatch[1].trim() : null;
      
      if (!baseField) {
        console.warn(`Could not parse base field from formula: ${formulaString}`);
        return;
      }

      if (isHierarchical) {
        // Build a map for quick parent lookup
        const rowMap = new Map<string, AnalyticsDataPoint>();
        result.forEach(row => {
          const rowId = row.id || '';
          if (rowId) {
            rowMap.set(rowId, row);
          }
        });

        // Create new array with calculated percentages relative to parent
        result = result.map(row => {
          const value = Number(row[baseField]) || 0;
          let percentage = 0;
          const rowLevel = row.level || 0;
          
          if (rowLevel === 0) {
            // Top level (Enterprise): percentage relative to sum of all enterprises
            const allEnterprises = result.filter(r => (r.level || 0) === 0);
            if (allEnterprises.length === 1) {
              percentage = 100;
            } else {
              const enterpriseTotal = allEnterprises.reduce((sum, ent) => {
                return sum + (Number(ent[baseField]) || 0);
              }, 0);
              if (enterpriseTotal > 0) {
                percentage = (value / enterpriseTotal) * 100;
              }
            }
          } else if (row.parentId) {
            // Child level: percentage relative to parent
            const parent = rowMap.get(row.parentId);
            if (parent) {
              const parentValue = Number(parent[baseField]) || 0;
              if (parentValue > 0) {
                percentage = (value / parentValue) * 100;
              }
            }
          }
          
          // Return new object with calculated percentage
          return {
            ...row,
            [column.field]: percentage
          };
        });
      } else {
        // Non-hierarchical: calculate percentage relative to total of all rows
        const total = result.reduce((sum, row) => {
          const value = Number(row[baseField]) || 0;
          return sum + value;
        }, 0);

        // Create new array with calculated percentages
        result = result.map(row => {
          const value = Number(row[baseField]) || 0;
          let percentage = 0;
          
          if (total > 0) {
            percentage = (value / total) * 100;
          }
          
          // Return new object with calculated percentage
          return {
            ...row,
            [column.field]: percentage
          };
        });
      }
    });
    
    return result;
  }

  static exportToCSV(
    data: AnalyticsDataPoint[], 
    columns: TableColumn[], 
    filename = 'analytics_data',
    options?: ExportOptions
  ) {
    const { headers, rows } = this.prepareTableData(data, columns, options);
    
    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell.replace(/\n/g, ' ')}"`).join(','))
    ].join('\n');

    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `${filename}.csv`);
  }

  static exportToXLSX(
    data: AnalyticsDataPoint[], 
    columns: TableColumn[], 
    filename = 'analytics_data',
    options?: ExportOptions
  ) {
    const { headers, rows } = this.prepareTableData(data, columns, options);
    
    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    
    // Set column widths
    const columnWidths = columns.map(col => ({ wch: Math.max(col.header.length, 15) }));
    worksheet['!cols'] = columnWidths;

    // Set cell styles for cells with variances (newlines) to wrap text
    // XLSX uses A1 notation for cell references
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
    
    // Iterate through all cells and set wrap text for cells containing newlines
    for (let row = 0; row <= range.e.r; row++) {
      for (let col = 0; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        const cell = worksheet[cellAddress];
        
        if (cell && cell.v && typeof cell.v === 'string' && cell.v.includes('\n')) {
          // Set wrap text style for cells with newlines (variances)
          cell.s = {
            ...cell.s,
            alignment: {
              ...(cell.s?.alignment || {}),
              wrapText: true,
              vertical: 'top'
            }
          };
          
          // Increase row height for rows with wrapped text
          if (!worksheet['!rows']) {
            worksheet['!rows'] = [];
          }
          if (!worksheet['!rows'][row]) {
            worksheet['!rows'][row] = {};
          }
          // Set row height to accommodate two lines (default is ~15, set to ~30 for wrapped text)
          worksheet['!rows'][row].hpt = 30;
        }
      }
    }

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Analytics Data');

    // Save file
    XLSX.writeFile(workbook, `${filename}.xlsx`);
  }

  static exportToPDF(
    data: AnalyticsDataPoint[], 
    columns: TableColumn[], 
    filename = 'analytics_data',
    options?: ExportOptions
  ) {
    const { headers, rows } = this.prepareTableData(data, columns, options);
    
    const doc = new jsPDF('l'); // landscape orientation for better table fit
    
    // Add title
    doc.setFontSize(16);
    doc.text('Analytics Report', 14, 22);
    
    // Add timestamp
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 30);

    // Create table
    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: 35,
      styles: {
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [66, 66, 66],
        textColor: [255, 255, 255],
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
      margin: { top: 35, left: 14, right: 14 },
      theme: 'grid',
    });

    // Save PDF
    doc.save(`${filename}.pdf`);
  }

  static shareViaEmail(
    data: AnalyticsDataPoint[], 
    columns: TableColumn[], 
    filename = 'analytics_data',
    options?: ExportOptions
  ) {
    // Generate XLSX for email attachment
    const { headers, rows } = this.prepareTableData(data, columns, options);
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Analytics Data');
    
    // Convert to base64 for email attachment
    const xlsxBuffer = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
    const blob = new Blob([xlsxBuffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    
    // Create data URL
    const reader = new FileReader();
    reader.onload = function(e) {
      const dataUrl = e.target?.result as string;
      
      // Create email content
      const subject = encodeURIComponent('Analytics Report');
      const body = encodeURIComponent(
        `Please find the attached analytics report.\n\n` +
        `Report generated: ${new Date().toLocaleDateString()}\n` +
        `Records: ${data.length}\n\n` +
        `Note: The attachment is in Excel format.`
      );
      
      // Open email client
      const mailtoUrl = `mailto:?subject=${subject}&body=${body}`;
      window.open(mailtoUrl);
      
      // Also download the file for manual attachment
      saveAs(blob, `${filename}.xlsx`);
    };
    
    reader.readAsDataURL(blob);
  }

  static getExportData(data: AnalyticsDataPoint[], columns: TableColumn[]) {
    return {
      recordCount: data.length,
      columnCount: columns.length,
      size: this.estimateDataSize(data, columns)
    };
  }

  private static estimateDataSize(data: AnalyticsDataPoint[], columns: TableColumn[]): string {
    const avgRowSize = 50; // rough estimate per row in bytes
    const totalSize = data.length * columns.length * avgRowSize;
    
    if (totalSize < 1024) return `${totalSize} B`;
    if (totalSize < 1024 * 1024) return `${(totalSize / 1024).toFixed(1)} KB`;
    return `${(totalSize / (1024 * 1024)).toFixed(1)} MB`;
  }
}